// Enhanced fetchWithCache with improved error handling and cache management

export async function fetchWithCache(url) {
  const API_CACHE_NAME = 'api-cache-v1';
  const OFFLINE_STORAGE_KEY = 'seabird_offline_data';

  // Environment-based configuration
  const isDevelopment = process.env.NODE_ENV === 'development';
  
  // Helper function to save to localStorage
  const saveToLocalStorage = (url, data) => {
    try {
      const offlineData = JSON.parse(localStorage.getItem(OFFLINE_STORAGE_KEY) || '{}');
      offlineData[url] = {
        data: data,
        timestamp: Date.now()
      };
      localStorage.setItem(OFFLINE_STORAGE_KEY, JSON.stringify(offlineData));
    } catch (error) {
      console.warn('Failed to save to localStorage:', error);
    }
  };

  // Helper function to get from localStorage
  const getFromLocalStorage = (url) => {
    try {
      const offlineData = JSON.parse(localStorage.getItem(OFFLINE_STORAGE_KEY) || '{}');
      return offlineData[url]?.data || null;
    } catch (error) {
      console.warn('Failed to read from localStorage:', error);
      return null;
    }
  };

  // Try network first, with comprehensive fallbacks
  try {
    const response = await fetch(url);
    if (response.ok) {
      const data = await response.json();
      
      // Save successful response to both cache and localStorage
      if ('caches' in window) {
        try {
          const cache = await caches.open(API_CACHE_NAME);
          await cache.put(url, new Response(JSON.stringify(data), {
            headers: { 'Content-Type': 'application/json' }
          }));
        } catch (cacheError) {
          console.warn('Failed to cache response:', cacheError);
        }
      }
      
      saveToLocalStorage(url, data);
      console.log(`🌐 Fresh data received and cached: ${url}`);
      return data;
    }
    throw new Error(`Network response was not ok: ${response.status}`);
  } catch (networkError) {
    console.warn(`Network failed for ${url}, trying fallbacks:`, networkError.message);
    
    // Fallback 1: Try Cache API
    if ('caches' in window) {
      try {
        const cache = await caches.open(API_CACHE_NAME);
        const cachedResponse = await cache.match(url);
        if (cachedResponse) {
          const data = await cachedResponse.json();
          console.log(`📦 Serving from cache: ${url}`);
          return data;
        }
      } catch (cacheError) {
        console.warn('Cache API failed:', cacheError);
      }
    }
    
    // Fallback 2: Try localStorage
    const localData = getFromLocalStorage(url);
    if (localData) {
      console.log(`💾 Serving from localStorage: ${url}`);
      return localData;
    }
    
    // Fallback 3: Provide sample/empty data for key endpoints
    if (url.includes('/api/recipes') && !url.includes('/recipe/')) {
      console.log(`🔄 Providing empty recipes array for offline mode`);
      return { success: true, data: [] };
    }
    
    if (url.includes('/api/food_categories')) {
      console.log(`🔄 Providing default categories for offline mode`);
      return { 
        success: true, 
        data: [
          { id: 1, name: 'Bars' },
          { id: 2, name: 'Bread' },
          { id: 5, name: 'Muffins' },
          { id: 9, name: 'Main Dishes' }
        ]
      };
    }
    
    // If all fallbacks fail, throw the original error
    throw new Error(`All fallbacks failed for ${url}: ${networkError.message}`);
  }
}